import React, { useMemo, useState, useEffect } from "react";
import { Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { postApiCall } from "../../../service/httpCall";
import { API_SMSWEBHOOK_CREATE } from "../../../service/url";
import { ROUTES } from "../../../utilities/constant";
import ApiBuilder from "./components/ApiBuilder";
import CopyButton from "./components/CopyButton";
import InstructionCard from "./components/InstructionCard";
import { toast } from "react-toastify";
import { fetchAndSetFlashMessage } from '../../../service/fetchFlashMessage';
import {FLASH_MESSAGE_ACTION_OBJECT, MODULE_ACL } from '../../../utilities/constant';

const CreateSMSWebhook = () => {
  const navigate = useNavigate();

  const [webhook_name, setWebhookName] = useState("");
  const [request_type, setRequestType] = useState("");

  const [url, setUrl] = useState("");
  const [sms_text, setSmsText] = useState("");
  const [payload, setPayload] = useState("");
  const [header, setHeader] = useState("");

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);

    useEffect(() => {
        fetchAndSetFlashMessage(
            msg => msg.module_name === MODULE_ACL.SMS_WEBHOOK && msg.action === FLASH_MESSAGE_ACTION_OBJECT.ADD && msg.type === 'Flash Message',
            setAlertMessage
        );
      }, []);
  const apiMethod = useMemo(() => {
    // UI request type should be GET/POST (like WebhooksSms/index_new.js)
    return request_type || "GET";
  }, [request_type]);



  const formattedHeader = useMemo(() => {
    const h = (header || "").trim();
    if (!h) return "";
    try {
      return JSON.stringify(JSON.parse(h), null, 2);
    } catch {
      return h;
    }
  }, [header]);



  const handleApiBuilderChange = (apiState) => {
    setUrl(apiState?.url || "");
    // Persist headers for GET/POST (backend column: sms_webhooks.header)
    if (!apiState) {
      setHeader("");
      return;
    }

    if (apiState.headerMode === "raw") {
      setHeader((apiState.rawHeaders || "").trim());
      return;
    }

    const headerObj = (apiState.headers || []).reduce((acc, h) => {
      const key = (h?.key || "").trim();
      if (!key) return acc;
      acc[key] = h?.value ?? "";
      return acc;
    }, {});
    setHeader(Object.keys(headerObj).length ? JSON.stringify(headerObj) : "");
  };

  const handleUseUrl = (u) => setUrl(u || "");

  const handleBuilderResponse = (bodyString) => {
    // Use the builder-generated body as payload template.
    setPayload(bodyString || "");
  };

  const validate = () => {
    const name = webhook_name.trim();
    const u = url.trim();
    const type = request_type;

    const urlRegex = /^(https?:\/\/)([\w\-._~:/?#[\]@!$&'()*+,;=]+)$/i;

    if (!name || !u || !type) {
      toast.error("Please fill all required fields.");
      return false;
    }
    if (!urlRegex.test(u)) {
      toast.error("Please enter a valid Webhook URL (must start with http:// or https://).");
      return false;
    }
    if (header && header.trim()) {
      try {
        JSON.parse(header);
      } catch {
        toast.error("Headers must be valid JSON (use Api Builder headers section).");
        return false;
      }
    }
    if (type === "GET" && !sms_text.trim()) {
      toast.error("SMS Text is required for GET requests.");
      return false;
    }
    if (type === "POST" && !payload.trim()) {
      toast.error("Payload is required for POST requests.");
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setShowConfirmModal(true);
  };

  const submitWebhook = async () => {
    setShowConfirmModal(false);
    setSaving(true);
    const apiRequestType = request_type === "POST" ? "WHATSAPP" : "GET";
    const body = {
      webhook_name: webhook_name.trim(),
      url: url.trim(),
      request_type: apiRequestType,
      sms_text: request_type === "GET" ? sms_text : null,
      payload: request_type === "POST" ? payload : null,
      header: header && header.trim() ? header.trim() : null,
      status: "1",
    };

    try {
      await postApiCall(API_SMSWEBHOOK_CREATE, body);
      toast.success("SMS Webhook created successfully!");
      navigate(`/${ROUTES.SMSWEBHOOK.LIST}`);
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {alertMessage && <div className="alert alert-success marquee-container" style={{ marginTop: "5px" }}>
        <span className="marquee-text" style={{'--marquee-duration': `${(alertMessage.length * 0.2) + 15}s`}}>{alertMessage}</span>
      </div>}
      <div className="page-wrapper">
        <Card className="page-card">
          <Card.Header className="page-header">Add SMS Webhook</Card.Header>
          <Card.Body>
            <Row className="g-4">
              <Col xs={12} md={7} className="left-panel">
                <Card className="section-card">
                  <Card.Body>
                    <Form onSubmit={handleSubmit}>
                      <Row>
                        <Col sm={6}>
                          <Form.Group className="form-group required">
                            <Form.Label>Webhook Name:<span style={{ color: "red", margin: "0 4px" }}>*</span></Form.Label>
                            <Form.Control
                              required
                              name="webhook_name"
                              type="text"
                              placeholder="Webhook Name"
                              value={webhook_name}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (/^[A-Za-z]*$/.test(value)) {
                                  setWebhookName(value);
                                }
                              }}
                              autoComplete="off"
                              pattern="^[A-Za-z]+$"
                              title="Only letters allowed. No numbers, spaces, or special characters."
                            />
                          </Form.Group>
                        </Col>
                        <Col sm={6}>
                          <Form.Group className="form-group required">
                            <Form.Label>Request Type:<span style={{ color: "red", margin: "0 4px" }}>*</span></Form.Label>
                            <Form.Select
                              required
                              name="request_type"
                              value={request_type}
                              onChange={(e) => setRequestType(e.target.value)}
                            >
                              <option value="">Select Request Type</option>
                              <option value="GET">GET</option>
                              <option value="POST">POST</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>

                      {request_type && (
                        <Row className="mb-2">
                          <Col>
                            <ApiBuilder
                              initialUrl={url}
                              initialMethod={apiMethod}
                              onUseUrl={handleUseUrl}
                              onMethod={(m) => {
                                // cURL paste can contain method; map to our UI GET/POST
                                const next =
                                  String(m || "").toUpperCase() === "GET" ? "GET" : "POST";
                                setRequestType(next);
                              }}
                              onResponse={handleBuilderResponse}
                              onStateChange={handleApiBuilderChange}
                            />
                          </Col>
                        </Row>
                      )}

                      <Row>
                        {request_type === "GET" && (
                          <Col className="position-relative">
                            <Form.Group className="form-group">
                              <Form.Label>
                                Note: Please paste the SMS text here <span style={{ color: "red", margin: "0 4px" }}>*</span>
                              </Form.Label>
                              <div className="d-flex justify-content-end mb-2">
                                <CopyButton text={sms_text} />
                              </div>
                              <Form.Control
                                required
                                name="sms_text"
                                value={sms_text}
                                as="textarea"
                                rows={6}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^\s+$/.test(value)) return;
                                    setSmsText(value);
                                }}
                                onBlur={(e) => {
                                  const trimmed = e.target.value.trim();
                                  setSmsText(trimmed);
                                }}
                              />
                            </Form.Group>
                          </Col>
                        )}

                        {request_type === "POST" && (
                          <Col className="position-relative">
                            <Form.Group className="form-group">
                              <Form.Label>
                                Note: Payload here and replace the Mobile No. with{" "}
                                {"{PHONE}"}
                              </Form.Label>
                              <div className="d-flex justify-content-end mb-2">
                                <CopyButton text={payload} />
                              </div>
                              <Form.Control
                                required
                                name="payload"
                                value={payload}
                                onChange={(e) => setPayload(e.target.value)}
                                as="textarea"
                                rows={6}
                              />
                            </Form.Group>
                          </Col>
                        )}
                      </Row>

                      {header && header.trim() && (
                        <Row>
                          <Col className="position-relative">
                            <Form.Group className="form-group">
                              <Form.Label>Note: Headers (JSON format)</Form.Label>
                              <div className="d-flex justify-content-end mb-2">
                                <CopyButton text={formattedHeader} />
                              </div>
                              <Form.Control
                                name="headers"
                                value={formattedHeader}
                                disabled
                                as="textarea"
                                rows={5}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      )}

                      <Button
                        variant="primary"
                        className="mt-3"
                        type="submit"
                        disabled={saving}
                      >
                        Add Sms Webhook
                      </Button>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} md={5} className="right-panel">
                <InstructionCard />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Submission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure the {request_type === "POST" ? "payload" : "sms text"} is
          correct?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
          >
            No
          </Button>
          <Button variant="primary" onClick={submitWebhook}>
            Yes
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateSMSWebhook;
